import { useEffect, useRef, useState } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { ConfigurationStatus } from '../domain/enums';
import {
  Configuration,
  CreateConfigurationInput,
  UpdateConfigurationInput,
} from '../domain/types';

/**
 * Write hooks for the configuration lifecycle.
 *
 * The module reads through `useAsyncResource` rather than a normalised
 * cache, so "invalidate the list" means "re-run the fetch that produced
 * it". Callers pass the `refetch` they want invalidated as `onSuccess` —
 * the list page passes `useConfigurations().refetch`, the detail page
 * passes `useConfiguration().refetch`, and the create page navigates
 * instead.
 */
export interface ConfigurationMutationOptions {
  onSuccess?: (configuration: Configuration) => void;
}

interface ConfigurationMutationState {
  isSaving: boolean;
  /** Message from the last failed attempt, cleared when a new one starts. */
  error: string | null;
}

export const CONFIGURATION_MUTATION_FALLBACK_ERROR =
  'Something went wrong. Please try again.';

const toErrorMessage = (error: unknown): string =>
  error instanceof Error && error.message
    ? error.message
    : CONFIGURATION_MUTATION_FALLBACK_ERROR;

interface ConfigurationMutation<TArgs extends unknown[]>
  extends ConfigurationMutationState {
  mutate: (...args: TArgs) => Promise<Configuration>;
  /**
   * Clears the last failure without running the mutation. The error is
   * otherwise kept until the next attempt, which is wrong once the operator
   * has abandoned that attempt (e.g. cancelled the dialog that showed it).
   */
  resetError: () => void;
}

/**
 * Shared save/loading/error plumbing. Rejections are re-thrown after being
 * recorded so a caller can still branch on failure (e.g. keep a dialog
 * open) rather than having to watch `error` in an effect.
 */
const useConfigurationMutation = <TArgs extends unknown[]>(
  run: (...args: TArgs) => Promise<Configuration>,
  onSuccess?: (configuration: Configuration) => void,
): ConfigurationMutation<TArgs> => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `onSuccess` routinely navigates away (create → detail), which unmounts
  // this hook before the `finally` below runs.
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const mutate = async (...args: TArgs): Promise<Configuration> => {
    setIsSaving(true);
    setError(null);

    try {
      const configuration = await run(...args);

      onSuccess?.(configuration);

      return configuration;
    } catch (caught) {
      if (isMountedRef.current) {
        setError(toErrorMessage(caught));
      }

      throw caught;
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  const resetError = (): void => setError(null);

  return { mutate, isSaving, error, resetError };
};

export interface UseCreateConfigurationReturn
  extends ConfigurationMutationState {
  createConfiguration: (
    input: CreateConfigurationInput,
  ) => Promise<Configuration>;
}

export const useCreateConfiguration = ({
  onSuccess,
}: ConfigurationMutationOptions = {}): UseCreateConfigurationReturn => {
  const client = usePartnerDataClient();
  const { mutate, isSaving, error } = useConfigurationMutation(
    (input: CreateConfigurationInput) => client.createConfiguration(input),
    onSuccess,
  );

  return { createConfiguration: mutate, isSaving, error };
};

export interface UseUpdateConfigurationReturn
  extends ConfigurationMutationState {
  updateConfiguration: (
    id: string,
    input: UpdateConfigurationInput,
  ) => Promise<Configuration>;
  /** Clears `error` without saving; see `ConfigurationMutation.resetError`. */
  resetError: () => void;
}

export const useUpdateConfiguration = ({
  onSuccess,
}: ConfigurationMutationOptions = {}): UseUpdateConfigurationReturn => {
  const client = usePartnerDataClient();
  const { mutate, isSaving, error, resetError } = useConfigurationMutation(
    (id: string, input: UpdateConfigurationInput) =>
      client.updateConfiguration(id, input),
    onSuccess,
  );

  return { updateConfiguration: mutate, isSaving, error, resetError };
};

export interface UseUpdateConfigurationStatusReturn
  extends ConfigurationMutationState {
  updateConfigurationStatus: (
    id: string,
    status: ConfigurationStatus,
  ) => Promise<Configuration>;
}

export const useUpdateConfigurationStatus = ({
  onSuccess,
}: ConfigurationMutationOptions = {}): UseUpdateConfigurationStatusReturn => {
  const client = usePartnerDataClient();
  const { mutate, isSaving, error } = useConfigurationMutation(
    (id: string, status: ConfigurationStatus) =>
      client.updateConfigurationStatus(id, status),
    onSuccess,
  );

  return { updateConfigurationStatus: mutate, isSaving, error };
};
